/**
 * Wharfkit Server-Side Service
 *
 * Provides server-side WAX blockchain operations using Wharfkit with private key signing.
 * This replaces the eosjs library to address the CVE-2024-968p security vulnerability.
 *
 * Key Features:
 * - Private key signing for server-side transactions
 * - RPC operations for blockchain queries
 * - Session management for treasury operations
 */

import {
  Session,
  SessionKit,
  LoginContext,
  LoginOptions,
  UserInterface,
  APIClient,
  CreateAccountContext,
  UserInterfaceLoginResponse,
  UserInterfaceAccountCreationResponse,
  PromptArgs,
  PromptResponse,
  UserInterfaceTranslateOptions,
  UserInterfaceTranslateFunction,
} from '@wharfkit/session';
import { Cancelable, cancelable, LocaleDefinitions } from '@wharfkit/common';
import { WalletPluginPrivateKey } from '@wharfkit/wallet-plugin-privatekey';
import { getWaxConfig, getTreasuryConfig, isTreasuryConfigured } from './config';
import { logger } from '../logger';

// WAX chain configurations for Wharfkit
const WAX_MAINNET_CHAIN = {
  id: '1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4',
  url: 'https://wax.greymass.com',
};

const WAX_TESTNET_CHAIN = {
  id: 'f16b1833c747c43682f4386fca9cbb327929334a762755ebec17f6f23c9b8a12',
  url: 'https://testnet.wax.pink.gg',
};

export interface TransactionResult {
  success: boolean;
  transactionId?: string;
  blockNumber?: number;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  traces?: any[]; // Action traces from the transaction for extracting inline action data
}

export interface RpcResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * WAX/EOSIO account information from get_account RPC call
 */
export interface WaxAccountInfo {
  account_name: string;
  head_block_num: number;
  head_block_time: string;
  privileged: boolean;
  last_code_update: string;
  created: string;
  core_liquid_balance?: string;
  ram_quota: number;
  net_weight: number;
  cpu_weight: number;
  net_limit: {
    used: number;
    available: number;
    max: number;
  };
  cpu_limit: {
    used: number;
    available: number;
    max: number;
  };
  ram_usage: number;
  permissions: Array<{
    perm_name: string;
    parent: string;
    required_auth: {
      threshold: number;
      keys: Array<{ key: string; weight: number }>;
      accounts: Array<{ permission: { actor: string; permission: string }; weight: number }>;
      waits: Array<{ wait_sec: number; weight: number }>;
    };
  }>;
  total_resources: {
    owner: string;
    net_weight: string;
    cpu_weight: string;
    ram_bytes: number;
  } | null;
  self_delegated_bandwidth: {
    from: string;
    to: string;
    net_weight: string;
    cpu_weight: string;
  } | null;
  refund_request: {
    owner: string;
    request_time: string;
    net_amount: string;
    cpu_amount: string;
  } | null;
  voter_info: Record<string, unknown> | null;
}

/**
 * WAX/EOSIO chain info from get_info RPC call
 */
export interface WaxChainInfo {
  server_version: string;
  chain_id: string;
  head_block_num: number;
  last_irreversible_block_num: number;
  last_irreversible_block_id: string;
  head_block_id: string;
  head_block_time: string;
  head_block_producer: string;
  virtual_block_cpu_limit: number;
  virtual_block_net_limit: number;
  block_cpu_limit: number;
  block_net_limit: number;
  server_version_string: string;
  fork_db_head_block_num: number;
  fork_db_head_block_id: string;
}

/**
 * Headless UI implementation for server-side use
 * Required by SessionKit but not used for server-side operations
 */
class HeadlessUI implements UserInterface {
  private translations: LocaleDefinitions = {};

  // Login flow methods
  async login(_context: LoginContext): Promise<UserInterfaceLoginResponse> {
    // Server-side sessions use private key wallet directly
    return { walletPluginIndex: 0 };
  }

  async onLogin(_options?: LoginOptions): Promise<void> {
    // No-op for server-side
  }

  async onLoginComplete(): Promise<void> {
    // No-op for server-side
  }

  // Account creation methods
  async onAccountCreate(_context: CreateAccountContext): Promise<UserInterfaceAccountCreationResponse> {
    // Account creation not supported in headless mode
    return {};
  }

  async onAccountCreateComplete(): Promise<void> {
    // No-op for server-side
  }

  // Error handling
  async onError(error: Error): Promise<void> {
    logger.error("Wharfkit server error", { error: error.message });
  }

  // Transaction flow methods
  async onTransact(): Promise<void> {
    // No-op for server-side
  }

  async onTransactComplete(): Promise<void> {
    // No-op for server-side
  }

  // Signing flow methods
  async onSign(): Promise<void> {
    // No-op for server-side
  }

  async onSignComplete(): Promise<void> {
    // No-op for server-side
  }

  // Broadcast flow methods
  async onBroadcast(): Promise<void> {
    // No-op for server-side
  }

  async onBroadcastComplete(): Promise<void> {
    // No-op for server-side
  }

  // Prompt method - returns a cancelable promise
  prompt(_args: PromptArgs): Cancelable<PromptResponse> {
    // Prompts not supported in headless mode
    return cancelable(Promise.resolve({}));
  }

  // Status update
  status(message: string): void {
    logger.debug("Wharfkit server status", { message });
  }

  // Translation methods
  translate(key: string, options?: UserInterfaceTranslateOptions, _namespace?: string): string {
    return options?.default ?? key;
  }

  getTranslate(_namespace?: string): UserInterfaceTranslateFunction {
    return (key: string, options?: UserInterfaceTranslateOptions) => {
      return options?.default ?? key;
    };
  }

  addTranslations(translations: LocaleDefinitions): void {
    this.translations = { ...this.translations, ...translations };
  }
}

/**
 * Server-side Wharfkit service for WAX blockchain operations
 * Uses private key authentication for signing transactions
 */
export class WharfkitServerService {
  private session: Session | null = null;
  private sessionKit: SessionKit | null = null;
  private config = getWaxConfig();
  private isInitialized = false;

  constructor() {
    // Constructor intentionally empty - lazy initialization
  }

  /**
   * Initialize the Wharfkit session with treasury private key
   * @throws Error if treasury not configured
   */
  async initialize(): Promise<void> {
    if (this.isInitialized && this.session) {
      return;
    }

    if (!isTreasuryConfigured()) {
      throw new Error('Treasury account not configured. Set WAX_TREASURY_ACCOUNT and WAX_TREASURY_PRIVATE_KEY environment variables.');
    }

    const treasuryConfig = getTreasuryConfig();

    logger.info("Initializing Wharfkit server", { treasuryAccount: treasuryConfig.treasuryAccount });

    // Get chain configuration based on network
    const chainConfig = treasuryConfig.network === 'testnet' ? WAX_TESTNET_CHAIN : WAX_MAINNET_CHAIN;

    // Override URL from config if provided
    const chain = {
      id: treasuryConfig.chainId || chainConfig.id,
      url: treasuryConfig.rpcEndpoint || chainConfig.url,
    };

    // Create SessionKit with private key wallet plugin and headless UI
    this.sessionKit = new SessionKit({
      appName: 'Scavenger Hunt SaaS Server',
      chains: [chain],
      ui: new HeadlessUI(),
      walletPlugins: [
        new WalletPluginPrivateKey(treasuryConfig.treasuryPrivateKey),
      ],
    });

    // Login to create session
    const loginResult = await this.sessionKit.login({
      chain: chain.id,
      permissionLevel: `${treasuryConfig.treasuryAccount}@active`,
    });

    this.session = loginResult.session;
    this.isInitialized = true;

    logger.info("Wharfkit session initialized", { actor: this.session.actor.toString() });
  }

  /**
   * Get the current session, initializing if needed
   */
  async getSession(): Promise<Session> {
    if (!this.session) {
      await this.initialize();
    }
    if (!this.session) {
      throw new Error('Failed to initialize session');
    }
    return this.session;
  }

  /**
   * Execute a blockchain transaction with the treasury account
   *
   * @param actions - Array of actions to execute
   * @returns Transaction result with ID and block number
   */
  async transact(actions: Array<{
    account: string;
    name: string;
    authorization: Array<{ actor: string; permission: string }>;
    data: Record<string, unknown>;
  }>): Promise<TransactionResult> {
    try {
      const session = await this.getSession();

      logger.debug("Executing Wharfkit transaction", { actionCount: actions.length });

      const result = await session.transact({
        actions,
      }, {
        broadcast: true,
        expireSeconds: 30,
      });

      const response = result.response;

      logger.info("Wharfkit transaction successful", { transactionId: response?.transaction_id });

      // Extract action traces for inline action data (e.g., lognewtempl for template ID)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const traces = (response?.processed as any)?.action_traces || [];

      return {
        success: true,
        transactionId: response?.transaction_id,
        blockNumber: response?.processed?.block_num,
        traces,
      };
    } catch (error) {
      logger.error("Wharfkit transaction failed", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown transaction error',
      };
    }
  }

  /**
   * Execute a single action transaction
   *
   * @param account - Contract account
   * @param name - Action name
   * @param data - Action data
   * @returns Transaction result
   */
  async executeAction(
    account: string,
    name: string,
    data: Record<string, unknown>
  ): Promise<TransactionResult> {
    const session = await this.getSession();
    const actor = session.actor.toString();

    return this.transact([
      {
        account,
        name,
        authorization: [{ actor, permission: 'active' }],
        data,
      },
    ]);
  }

  /**
   * Get the treasury account name
   */
  async getTreasuryAccount(): Promise<string> {
    const session = await this.getSession();
    return session.actor.toString();
  }

  /**
   * Get account information from the blockchain
   *
   * @param accountName - Account name to query
   * @returns Account info or error
   */
  async getAccount(accountName: string): Promise<RpcResult<WaxAccountInfo>> {
    try {
      const session = await this.getSession();
      const accountInfo = await session.client.v1.chain.get_account(accountName);

      return {
        success: true,
        data: accountInfo as unknown as WaxAccountInfo,
      };
    } catch (error) {
      logger.error("Wharfkit failed to get account", {
        accountName,
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get account',
      };
    }
  }

  /**
   * Get currency balance for an account
   *
   * @param accountName - Account name to query
   * @param contract - Token contract (default: eosio.token)
   * @param symbol - Token symbol (default: WAX)
   * @returns Balance strings or error
   */
  async getCurrencyBalance(
    accountName: string,
    contract: string = 'eosio.token',
    symbol: string = 'WAX'
  ): Promise<RpcResult<string[]>> {
    try {
      const session = await this.getSession();
      const balances = await session.client.v1.chain.get_currency_balance(contract, accountName, symbol);

      return {
        success: true,
        data: balances.map((b: { toString: () => string }) => b.toString()),
      };
    } catch (error) {
      logger.error("Wharfkit failed to get balance", {
        accountName,
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get balance',
      };
    }
  }

  /**
   * Get blockchain info
   *
   * @returns Chain info or error
   */
  async getInfo(): Promise<RpcResult<WaxChainInfo>> {
    try {
      const session = await this.getSession();
      const info = await session.client.v1.chain.get_info();

      return {
        success: true,
        data: info as unknown as WaxChainInfo,
      };
    } catch (error) {
      logger.error("Wharfkit failed to get chain info", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get chain info',
      };
    }
  }

  /**
   * Check if the service is properly configured
   */
  isConfigured(): boolean {
    return isTreasuryConfigured();
  }

  /**
   * Reset the session (for testing or reconfiguration)
   */
  resetSession(): void {
    this.session = null;
    this.sessionKit = null;
    this.isInitialized = false;
  }
}

/**
 * RPC-only client for read operations that don't require signing
 * Uses Wharfkit's APIClient for blockchain queries
 */
export class WharfkitRpcClient {
  private apiClient: APIClient | null = null;
  private currentRpcEndpoint: string | null = null;

  /**
   * Get the API client, initializing if needed
   * Reads config lazily to ensure environment variables are available
   */
  private getClient(): APIClient {
    const config = getWaxConfig();

    // Re-create client if RPC endpoint changed (e.g., env var was updated)
    if (this.apiClient && this.currentRpcEndpoint === config.rpcEndpoint) {
      return this.apiClient;
    }

    logger.debug("Initializing Wharfkit RPC client", {
      rpcEndpoint: config.rpcEndpoint,
      network: config.network,
    });

    this.currentRpcEndpoint = config.rpcEndpoint;
    this.apiClient = new APIClient({
      url: config.rpcEndpoint,
    });

    return this.apiClient;
  }

  /**
   * Get account information
   */
  async getAccount(accountName: string): Promise<RpcResult<WaxAccountInfo>> {
    try {
      const client = this.getClient();
      const accountInfo = await client.v1.chain.get_account(accountName);

      return {
        success: true,
        data: accountInfo as unknown as WaxAccountInfo,
      };
    } catch (error) {
      // Check if account doesn't exist (expected error for availability checks)
      if (error instanceof Error && error.message.includes('unknown key')) {
        return {
          success: false,
          error: 'Account not found',
        };
      }
      logger.error("Wharfkit RPC failed to get account", {
        accountName,
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get account',
      };
    }
  }

  /**
   * Get currency balance for an account
   */
  async getCurrencyBalance(
    accountName: string,
    contract: string = 'eosio.token',
    symbol: string = 'WAX'
  ): Promise<RpcResult<string[]>> {
    try {
      const client = this.getClient();
      const balances = await client.v1.chain.get_currency_balance(contract, accountName, symbol);

      return {
        success: true,
        data: balances.map((b: { toString: () => string }) => b.toString()),
      };
    } catch (error) {
      logger.error("Wharfkit RPC failed to get balance", {
        accountName,
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get balance',
      };
    }
  }

  /**
   * Get blockchain info
   */
  async getInfo(): Promise<RpcResult<WaxChainInfo>> {
    try {
      const client = this.getClient();
      const info = await client.v1.chain.get_info();

      return {
        success: true,
        data: info as unknown as WaxChainInfo,
      };
    } catch (error) {
      logger.error("Wharfkit RPC failed to get chain info", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get chain info',
      };
    }
  }

  /**
   * Check if an account exists on the blockchain
   */
  async accountExists(accountName: string): Promise<boolean> {
    const result = await this.getAccount(accountName);
    return result.success;
  }
}

// Singleton instances
let serverServiceInstance: WharfkitServerService | null = null;
let rpcClientInstance: WharfkitRpcClient | null = null;

/**
 * Get or create the server service singleton
 */
export function getWharfkitServerService(): WharfkitServerService {
  if (!serverServiceInstance) {
    serverServiceInstance = new WharfkitServerService();
  }
  return serverServiceInstance;
}

/**
 * Get or create the RPC client singleton
 */
export function getWharfkitRpcClient(): WharfkitRpcClient {
  if (!rpcClientInstance) {
    rpcClientInstance = new WharfkitRpcClient();
  }
  return rpcClientInstance;
}

// Export default instances
export const wharfkitServerService = getWharfkitServerService();
export const wharfkitRpcClient = getWharfkitRpcClient();
